import {
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Box, HStack, Space} from 'platform/foundation';
import {z} from 'zod';

import {usePatchOfferValidityMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {formatIsoDate} from '../ServiceVehicleForm/utils/formatIsoDate';

interface OfferValidityDialogProps {
  businessCaseId: string;
  currentOfferValidTo?: string | null;
}

const OfferValidityFormSchema = z.object({
  offerValidTo: z.date().nullable(),
});

type OfferValidityFormValues = z.infer<typeof OfferValidityFormSchema>;

export function OfferValidityDialog(props: OfferValidityDialogProps) {
  const [patchOfferValidity] = usePatchOfferValidityMutation();

  const handleSubmit: FormSubmitHandler<OfferValidityFormValues> = (values) =>
    patchOfferValidity({
      businessCaseId: props.businessCaseId,
      body: {
        offerValidTo: formatIsoDate(values.offerValidTo),
      },
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
      })
      .catch(handleApiError);

  const defaultValues: Partial<OfferValidityFormValues> = {
    offerValidTo: parseDate(props.currentOfferValidTo) ?? undefined,
  };

  return (
    <Form<OfferValidityFormValues>
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      experimentalZodSchema={OfferValidityFormSchema}
    >
      {(control) => (
        <Box paddingTop={4} paddingBottom={4}>
          <FormField
            control={control}
            type="date"
            name="offerValidTo"
            label={i18n.t('entity.offerValidityDialog.labels.validTo')}
            isRequired
          />

          <Space vertical={4} />

          <HStack spacing={3} justify="flex-end">
            <FormButton
              control={control}
              type="button"
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={closeCurrentDialog}
            />
            <FormButton control={control} type="submit" title={i18n.t('general.actions.save')} />
          </HStack>
        </Box>
      )}
    </Form>
  );
}
